import React, { useState, useEffect } from 'react';
import store, { hmdaActions } from '@apply/adminStore';
import Dialog from '@material-ui/core/Dialog';
import { ConfirmModal } from '@apply/ui';
import { HmdaApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { useSelector } from 'react-redux';
import { BfiCustomStore, BfiDataGrid, Column } from '../../common/SharedComponents';
import debounce from 'lodash.debounce';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const ManageDeletedModal = () => {
    const { isOpen } = useSelector((state) => state.hmda.hmdaManageDeletedModal);
    const { dispatch } = store;
    const gridStore = new BfiCustomStore();
    const [dataStore, set_dataStore] = useState(undefined);

    const hmdaApi = new HmdaApi.HmdaApi(applicationInsights);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [searchText, set_searchText] = useState('');
    const [restoreConfirmText, set_confirmRestoreText] = useState('');
    const [openConfirmRestore, set_openConfirmRestore] = useState(false);
    const [selectedId, set_selectedId] = useState(0);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            marginRight: '20px',
            marginLeft: '20px',
            textAlign: 'right',
        },
    };

    const handleClose = () =>
        dispatch(
            hmdaActions.setHmdaManageDeletedModal({
                hmdaManageDeletedModal: { isOpen: false },
            })
        );

    const dataFunction = async (criteria) => {
        set_loadingOpen(true);
        const manageableDeletedHMDARecordsResponse = await hmdaApi.getManageableDeletedHMDARecords(criteria);
        set_loadingOpen(false);
        if (manageableDeletedHMDARecordsResponse.statusCode !== 200) return;
        return manageableDeletedHMDARecordsResponse;
    };

    const applySearch = (searchText) => {
        set_searchText(searchText);
        gridStore.setOnLoad('id', dataFunction, searchText);
        set_dataStore(gridStore.store);
    };

    const _onSearchChange = (event) => {
        debouncedSearch(event.target.value);
    };

    const debouncedSearch = debounce(applySearch, 500);

    const onRenderDate = (e) => {
        if (!e.value) {
            return null;
        }
        return new Date(e.value).toLocaleDateString('en-us');
    };

    const onRestore = async (event, data) => {
        set_selectedId(data.id);
        set_confirmRestoreText(`Are you sure you want to restore the selected record (${data.bfiNumber})?`);
        set_openConfirmRestore(true);
    };

    const onConfirmRestore = () => {
        if (selectedId > 0) {
            restoreHMDARecord(selectedId);
        }
        clearSelections();
    };

    const onCancelRestore = () => {
        clearSelections();
    };

    const clearSelections = () => {
        set_selectedId(0);
        set_confirmRestoreText('Nothing selected for restore');
        set_openConfirmRestore(false);
    };

    const restoreHMDARecord = async (selectedId) => {
        try {
            set_loadingMessage('Saving changes...');
            set_loadingOpen(true);
            const response = await hmdaApi.restoreManuallyDeletedHMDARecord(selectedId);
            if (response.statusCode !== 200) {
                return;
            }

            handleAdminSuccess('HMDA Record restored successfully.');
            applySearch();
            set_loadingOpen(false);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const onUndoDeleteRender = (e) => {
        return (
            <button
                className='button hollow'
                onClick={(event) => onRestore(event, e.data)}
                title='Restore HMDA Record'
                style={{ margin: '0' }}
            >
                <FontAwesomeIcon icon={faThumbsUp} /> Restore
            </button>
        );
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchText);
            set_dataStore(gridStore.store);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='md'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{
                            width: '800px',
                            maxHeight: '500px',
                            overflow: 'unset',
                        }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1>Manually Deleted Records</h1>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={handleClose}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                        </div>
                        <div
                            className='main-content'
                            style={{ padding: '10px', width: '100%' }}
                        >
                            <div style={{ textAlign: 'center' }}>Only HMDA entries with BFI Numbers are shown:</div>
                            <div className='input-has-icon main-search'>
                                <input
                                    type='search'
                                    placeholder='Search on BFI Number, Applicant Name, or Entity Name...'
                                    defaultValue={searchText}
                                    onChange={(event) => _onSearchChange(event)}
                                />
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    aria-hidden='true'
                                    className='icon'
                                />
                            </div>

                            <BfiDataGrid
                                defaultPageSize={15}
                                dataStore={dataStore}
                                applicationInsights={applicationInsights}
                                noDataText='No HMDA Records found'
                                width={'100%'}
                                maxHeight={'350'}
                            >
                                <Column
                                    dataField='id'
                                    visible={false}
                                />
                                <Column
                                    dataField='applicationId'
                                    visible={false}
                                />
                                <Column
                                    dataField='bfiNumber'
                                    caption='BFI #'
                                    dataType='string'
                                    width={150}
                                />
                                <Column
                                    dataField='createdAt'
                                    caption='Application Created'
                                    cellRender={onRenderDate}
                                    width={150}
                                />
                                <Column
                                    dataField='applicantName'
                                    caption='Applicant Name'
                                    width={180}
                                />
                                <Column
                                    dataField='entityName'
                                    caption='Entity Name'
                                    width={180}
                                />
                                <Column
                                    caption='Undo Delete'
                                    cellRender={onUndoDeleteRender}
                                    width={40}
                                    alignment='center'
                                />
                            </BfiDataGrid>
                        </div>
                        <div
                            className='button-footer'
                            style={styles.buttonContainer}
                        >
                            <button
                                type='button'
                                className='button hollow'
                                data-dismiss='modal'
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ConfirmModal
                isOpen={openConfirmRestore}
                text={restoreConfirmText}
                confirmCallback={onConfirmRestore}
                cancelCallback={onCancelRestore}
            />
        </>
    );
};

export default ManageDeletedModal;
